<template>
  <div class="flex bg-grey-900 h-full" style="min-height: 600px">
    <div class="w-9/12 p-4 bg-grey-800">
      <div class="mb-4">
        <div class="text-white">
          <div class="mb-3">
            <span class="font-semibold mb-4 text-lg">{{ $t('frontend_original_image') }}</span>
            <p v-if="originalSize.width" class="font-light text-sm">
              <span>{{$t('frontend_images_dimensions')}}: </span>
              {{ originalSize.width || '?' }}px x {{ originalSize.height || '?' }}px
            </p>
            <p v-if="originalSize.size" class="font-light text-sm"><span>{{$t('frontend_images_size')}}: </span> {{ originalSize.size || '?' }}kb</p>
          </div>
        </div>
      </div>
      <cropper ref="cropper" :src="baseUrl" :canvas="false" :resize-image="false" :move-image="false" :default-size="defaultSize" @change="updateConfig('ratio')" />
    </div>
    <div class="w-3/12 p-4">
      <div class="mb-4">
        <span class="text-white font-semibold mb-4 text-lg">{{$t('frontend_images_desired_dimensions')}}</span>
        <div class="flex gap-2 pt-2">
          <div class="flex-1 mr-5">
            <div class="text-white text-sm ">{{ $t('frontend_images_width') }}</div>
            <text-input v-model="config.resize.width" type="number" dark class="h-8" @input="updateConfig('width')"/>
          </div>
          <div class="flex-1 ml-5">
            <div class="text-white text-sm">{{ $t('frontend_images_height') }}</div>
            <text-input v-model="config.resize.height" type="number" dark class="h-8" @input="updateConfig('height')"/>
          </div>
        </div>
        <div>
          <div class="lock">
            <gd-button :primary="forceRatio" :dark="!forceRatio" :class="{dfbg:!forceRatio}" size="icon" @click="toggleRatio"><i class="fa fa-lock"></i></gd-button>
          </div>
        </div>
      </div>
      <div class="my-4">
        <div class="text-white">
          <div class="my-3">
            <span class="font-semibold mb-4 text-lg">{{$t('frontend_images_final')}}</span>
            <p v-if="dimensions.width" class="font-light text-sm">
              <span>Dimensions: </span>
              {{ dimensions.width || '?' }}px x {{ dimensions.height || '?' }}px
            </p>
            <p v-if="dimensions.size" class="font-light text-sm"><span>{{$t('frontend_images_size')}}: </span> {{ dimensions.size || '?' }}kb</p>
          </div>
          <img :src="imageUrl" class="my-2 max-h-48">
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.vue-advanced-cropper__background, .vue-advanced-cropper__foreground {
    background-color: rgba(41, 43, 73, 1);;
}

.lock {
  position: relative;
  text-align: center;
  display: flex;
  justify-content: center;
  width: 224px;
  margin: auto;

  &:before {
    border-left: 1px solid #545671;
    border-right: 1px solid #545671;
    border-bottom: 1px solid #545671;
    border-radius: 0 0 10px 10px;
    content: "";
    width: 100%;
    height: 14px;
    position: absolute;
    left: 0;
    z-index: 1;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  > button.dfbg {
    background-color: #191c3c;
  }
}
</style>
<script>
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { debounce } from 'lodash-es';


import ImagePicker from './ImagePicker';
import GdButton from './Button';
import GdDropdown from './Dropdown';
import TextInput from './TextInput';
import GdToggle from '@groovepages/gd-ui-kit/components/Toggle';
import {gdImgProxy} from '../utils';

export default {
  name: 'ImagePickerEditor',
  components: {
    GdDropdown,
    GdToggle,
    GdButton,
    ImagePicker,
    TextInput,
    Cropper
  },
  props: {
    value: {
      type: String,
      default: true
    }
  },
  data() {
    return {
      image: undefined,
      dimensions: {
        width: undefined,
        height: undefined,
        size: undefined
      },
      originalSize: {
        width: undefined,
        height: undefined,
        size: undefined
      },
      config: {
        resize: {
          mode: 'force',
          width: null,
          height: null,
        },
        crop: {
          width: undefined,
          height: undefined
        },
        gravity: {
          type: 'nw',
          offset_x: 0,
          offset_y: 0
        }
      },
      loaded: true,
      forceRatio: true
    };
  },
  computed: {
    baseUrl() {
      if( this.image )
        return gdImgProxy({
          url: this.image,
          height: 600
        });

      return undefined
    },
    imageUrl() {
      if( this.image ) {
        const { height, width } = this.config.resize;
        const { height: cropHeight, width: cropWidth } = this.config.crop;
        const { type: gravity_type, offset_x, offset_y } = this.config.gravity;
  
        let proxyDatas = {url: this.image,enlarge:1};
  
        if( height ){
          proxyDatas = {...proxyDatas,...{
            width: width,
            height: height,
            resize: 'force'
          }};
        }
  
        if( gravity_type ){
          proxyDatas = {...proxyDatas,...{
            gravity: gravity_type,
            gravity_offset_x: offset_x,
            gravity_offset_y: offset_y
          }};
        }
  
        if( cropHeight || cropWidth ){
          proxyDatas = {...proxyDatas,...{
            crop_width: cropWidth || this.originalSize.width,
            crop_height: cropHeight || this.originalSize.height
          }};
        }
  
        return gdImgProxy(proxyDatas)
      }

      return undefined;
    }
  },
  watch: {
    value: 'extractParts',
    imageUrl: 'getImageDimensionsAndSize'
  },
  mounted() {
    window.vm = this;
    this.extractParts();
  },
  methods: {
    async getOriginalDimensionsAndSize() {
      this.originalSize = await this.getDimensionsAndSize(gdImgProxy({url: this.image}));
      const { height, width } = this.$refs.cropper.imageAttributes;
      this.$refs.cropper.setCoordinates({
        top: this.config.gravity.offset_y ? Math.round(this.config.gravity.offset_y / this.originalSize.height * height) : 0,
        left: this.config.gravity.offset_x ? Math.round(this.config.gravity.offset_x / this.originalSize.width * width) : 0,
        height: this.config.crop.height ? Math.round(this.config.crop.height / this.originalSize.height * height) : height,
        width: this.config.crop.width ? Math.round(this.config.crop.width / this.originalSize.width * width) : width
      });
    },
    getImageDimensionsAndSize: debounce(async function(){
      if (this.image) this.dimensions = await this.getDimensionsAndSize(this.imageUrl);
    },700),
    getDimensionsAndSize(url) {
      if( this.loaded )
      return new Promise(async (resolve) => {
        const dimensions = { size: undefined, height: undefined, width: undefined };
        /*
        const response = await fetch(url);

        // get file size
        const byteSize = parseInt(response.headers.get('content-length'));
        if (byteSize && !isNaN(byteSize)) {
          dimensions.size = Math.round(byteSize / 1024);
        }
        */

        // Get image dimensions
        const img = new Image();
        img.onload = () => {
          dimensions.height = img.height;
          dimensions.width = img.width;
          resolve(dimensions);
        };
        img.src = url;
      });
    },
    defaultSize({ imageSize, visibleArea }) {
      return {
        width: ( visibleArea || imageSize ).width,
        height: ( visibleArea || imageSize ).height
      };
    },
    async extractParts() {
      this.image = (this.value.includes("https:")?'':'https:')+this.value;
      const path = this.value.replace(window.gdEnv.VUE_APP_IMAGES_URL, '');
      const lessToken = path.substr(path.indexOf('/', path.indexOf('/', 1)));
      this.config = lessToken
          .substr(1, lessToken.indexOf('/plain'))
          .split('/')
          .reduce((config, item) => {
            const [type, ...params] = item.split(':');
            switch (type) {
              case 'rs':
                config.resize = {
                  mode: params[0],
                  width: params[1],
                  height: params[2]
                };
                break;
              case 'crop':
                config.crop = {
                  width: params[0],
                  height: params[1]
                };
                break;
              case 'gravity':
                config.gravity = {
                  type: params[0],
                  offset_x: params[1],
                  offset_y: params[2]
                };
                break;
            }
            return config;
          }, {
            resize: { mode: 'fit', height: undefined, width: undefined },
            crop: { height: undefined, width: undefined },
            gravity: { type: 'nowe', offset_x: 0, offset_y: 0 }
          });
      await this.getOriginalDimensionsAndSize();
    },
    updateConfig(updating = null) {
      if (!this.image || !this.originalSize.width) return;

      const { width, height, top, left } = this.$refs.cropper.coordinates;
      const image = this.$refs.cropper.image;
      this.config.gravity.type = 'nowe';
      this.config.gravity.offset_x = left ? Math.round(this.originalSize.width * left / image.width) : left;
      this.config.gravity.offset_y = top ? Math.round(this.originalSize.height * top / image.height) : top;

      const cropWidth = Math.round(this.originalSize.width * width / image.width);
      const cropHeight = Math.round(this.originalSize.height * height / image.height);
      this.config.crop.width = this.originalSize.width > cropWidth ? cropWidth : 0;
      this.config.crop.height = this.originalSize.height > cropHeight ? cropHeight : 0;
        
      if( updating == 'ratio' ){
        this.config.resize.width = this.config.crop.width || this.originalSize.width;
        this.config.resize.height = this.config.crop.height || this.originalSize.height;

        this.forceRatioval = this.config.resize.width / this.config.resize.height;
      }else if( ['width','height'].includes(updating) ){
        if( this.forceRatio ){
          if( updating == 'height' ) this.config.resize.width = Math.round(this.forceRatioval * this.config.resize.height);
          if( updating == 'width' ) this.config.resize.height = Math.round(this.config.resize.width / this.forceRatioval);
        }
      }
    },
    saveit() {
      this.$emit('input', this.imageUrl);
      this.$emit('close');
    },
    toggleRatio() {
      this.forceRatio = !this.forceRatio;
    }
  }
};
</script>
